/* eslint-disable react/no-array-index-key */
/* eslint-disable no-case-declarations */
/* eslint-disable no-underscore-dangle */
import { BrandButton, FullWidthTextSection, Heading, getTypedPatternVariants } from '@okam/brand-ui'
import { useIsClient } from '@okam/shared-ui/index'
import { useTranslation } from 'react-i18next'
import NewsLayout from '@/components/flexible-content/NewsLayout/NewsLayout'
import ProfessionalLayout from '@/components/flexible-content/ProfessionalLayout/ProfessionalLayout'
import type { TFlexibleContent, TNewsLayout } from '@/components/flexible-content/types'
import { MainLayout } from '@/components/layout'
import { useFragment } from 'gql'
import type {
  ExpertiseQuery,
  Expertise_Expertisefields_ExpertiseMembers,
  SiteOptionsQuery,
  LastPostsQuery,
  Maybe,
  MainMenuFragment,
  MemberType,
} from 'gql/graphql'
import { allMembersFragment } from 'query/fragments'
import tw, { theme } from 'twin.macro' /* eslint-disable-line */
import { MEMBERS_TYPE_DISPLAY, PROFESSIONAL_LAYOUT_MEMBERS_COUNT } from '../../../../const'
import FlexibleContentDispatcher from '../../../components/flexible-content/FlexibleContentDispatcher/FlexibleContentDispatcher'

export interface TExpertisePage {
  pageProps: ExpertiseQuery & SiteOptionsQuery
}

const ExpertisePage = (props: TExpertisePage) => {
  const { pageProps } = props
  const { expertise, siteOptions, mainMenu } = pageProps
  const expertiseMembers = expertise?.expertiseFields?.expertiseMembers as Expertise_Expertisefields_ExpertiseMembers[]
  const expertiseTypes = expertise?.expertiseTypes?.nodes
  const firstExpertiseType = expertiseTypes ? expertiseTypes[0] : null
  const allMembers = useFragment(allMembersFragment, pageProps.members)

  const { t } = useTranslation('common')
  const { isClient } = useIsClient()

  const okamContent = pageProps.expertise?.flexibleContent?.okamContent as Array<TFlexibleContent>

  return (
    <MainLayout
      siteOptionsProps={siteOptions}
      seo={expertise?.seo}
      allMembers={allMembers}
      translatedUri={expertise?.translatedUri}
      menuItems={mainMenu as MainMenuFragment}
    >
      <header>
        <FullWidthTextSection
          bgPattern={getTypedPatternVariants(expertise?.expertiseFields?.expertisePattern)}
          colorTheme={theme('colors.tertiary.6')}
          minHeight="500px"
          patternPosition="right"
          xAlign="left"
          yAlign="bottom"
        >
          {isClient && firstExpertiseType && (
            <BrandButton
              as="a"
              href={`/${t('ROUTES.EXPERTISE')}?expertisetype=${firstExpertiseType.slug}`}
              tw="!px-0 w-fit"
              buttonStyle="hollow"
              icon="ArrowLeft"
              iconPosition="left"
              variant="white"
            >
              {firstExpertiseType.name}
            </BrandButton>
          )}
          <Heading as="h1" variant="h1" tw="text-white">
            {expertise?.title}
          </Heading>
        </FullWidthTextSection>
      </header>
      {okamContent?.map((content: TFlexibleContent, index: number) => {
        const type = content?.__typename
        const members = expertiseMembers
          ?.filter((member) =>
            member.memberTypes?.nodes?.some((memberType: MemberType) =>
              MEMBERS_TYPE_DISPLAY.includes(memberType.slug ?? ''),
            ),
          )
          ?.sort(() => 0.5 - Math.random())
          ?.slice(0, PROFESSIONAL_LAYOUT_MEMBERS_COUNT) as Expertise_Expertisefields_ExpertiseMembers[]
        switch (type) {
          case 'Expertise_Flexiblecontent_OkamContent_NewsLayout':
            const newsLayoutProps = { ...pageProps, ...content } as TNewsLayout & Maybe<LastPostsQuery>
            return <NewsLayout key={`page-flexibleContent-${content?.fieldGroupName}-${index}`} {...newsLayoutProps} />
          case 'Expertise_Flexiblecontent_OkamContent_ProfessionalExpertiseLayout':
            return (
              <ProfessionalLayout
                key={`page-flexibleContent-${content?.fieldGroupName}-${index}`}
                {...content}
                expertiseMembers={members}
              />
            )
          default:
            return (
              <FlexibleContentDispatcher
                content={content}
                allMembers={allMembers}
                key={`expertise-flexibleContent-${content?.fieldGroupName}-${index}`}
              />
            )
        }
      })}
    </MainLayout>
  )
}

export default ExpertisePage
